import React, { useCallback, useContext, useEffect, useMemo } from "react";
import * as Sentry from "@sentry/react";
import { RootStoreContext } from "src/stores/RootStore";
import { observer } from "mobx-react-lite";
import { UI } from "@wwimmo/ui";
import { useTranslation } from "react-i18next";
import styles from "./AssemblyVoting.module.css";

interface IAssemblyVotingBase {
    assemblyid?: string;
}

const AssemblyVotingBase = observer((props: IAssemblyVotingBase) => {
    const { votingStore } = useContext(RootStoreContext);
    const { t } = useTranslation();

    useEffect(() => {
        if (votingStore.selectedVoting.votingid) {
            votingStore.loadVotingTenantsAndOptions();
        }
        // eslint-disable-next-line
    }, [votingStore.selectedVoting.votingid]);

    const tenantRows = useMemo(() => {
        return votingStore.votingTenants.map((tenant) => {
            const result = votingStore.votingResults.find((r) => r.tenantid === tenant.tenantid);
            const optionIndex = result?.votingoptionid
                ? votingStore.votingOptions.findIndex((opt) => opt.votingoptionid === result.votingoptionid)
                : -1;

            return {
                id: tenant.tenantid,
                label: tenant.name,
                selectedRow: optionIndex >= 0 ? optionIndex + 1 : 0
            };
        });
    }, [votingStore.votingTenants, votingStore.votingResults, votingStore.votingOptions]);

    const votingRenderKey = useMemo(() => {
        return `${votingStore.selectedVoting.votingid}-${votingStore.votingResults.length}-${votingStore.votingOptions.length}`;
    }, [votingStore.selectedVoting.votingid, votingStore.votingResults.length, votingStore.votingOptions.length]);

    const handleSelectionChange = useCallback(
        (data: { id: string; selectedColumn: number | null }[]) => {
            votingStore.setVotingComponentResponse(data);
        },
        [votingStore]
    );

    const handleSave = useCallback(() => {
        votingStore.saveVotingResult();
    }, [votingStore]);

    if (
        votingStore.votingList.length === 0 ||
        !votingStore.selectedVoting.votingid ||
        votingStore.votingTenants.length === 0 ||
        votingStore.votingOptions.length === 0 ||
        votingStore.votingResults.length === 0
    ) {
        return null;
    }

    return (
        <UI.Card>
            <div className={styles.VotingTitle}>{votingStore.selectedVoting.question}</div>
            <UI.Voting
                key={votingRenderKey}
                numberOfColumn={votingStore.votingOptions.length}
                headers={["WQ", ...votingStore.votingOptions.map((o) => o.text)]}
                withMainRow={true}
                rows={tenantRows}
                secondRowLabels={votingStore.votingTenants.map((tenant) => tenant.unitvalue.toString())}
                buttonColors={["#194d00", "#f8a300", "#900000", "#004997", "#650097"]}
                onSelectionChange={handleSelectionChange}
                withSearchInput={true}
            />
            <div className={styles.SaveButtonWrapper}>
                <UI.Button
                    onClick={handleSave}
                    className={styles.SaveButton}
                    label={t("labels.save")}
                    disabled={!votingStore.hasVotingResultChanged}
                />
            </div>
        </UI.Card>
    );
});

export const AssemblyVoting = Sentry.withProfiler(AssemblyVotingBase);
